import dayjs, { Dayjs } from 'dayjs';

/**
 * Sums the values and returns the result only if all inputs are non-null, otherwise return null.
 * @param values The values to be summed.
 * @returns The sum of all values or null if any value is null or undefined.
 */
export function sumIfAllNotNull(...values: (number | null | undefined)[]): number | null {
    if (values.length === 0) {
        return null;
    }

    let sum = 0;
    for (const num of values) {
        if (num === null || num === undefined) {
            return null;
        } else {
            sum += num;
        }
    }

    return sum;
}

/**
 * Format a day as YYYYMMDD.
 * Time is not included but should be assumed to be local time, not UTC.
 * @param date The date to format.
 * @returns The formatted date.
 */
export function formatYYYYMMDD(date: Dayjs | Date): string {
    if (date instanceof dayjs) {
        return (date as Dayjs).format('YYYYMMDD');
    } else {
        const jsDate = date as Date;
        const year = jsDate.getFullYear();
        const month = String(jsDate.getMonth() + 1).padStart(2, '0');
        const day = String(jsDate.getDate()).padStart(2, '0');
        return `${year}${month}${day}`;
    }
}

/**
 * Parse a date in YYYYMMDD format.
 * @param value  The date to parse.
 * @returns A valid Dayjs instance or null.
 */
export function parseYYYYMMDD(value: string): Dayjs | null {
    const date = dayjs(value, 'YYYYMMDD');
    return date.isValid() ? date : null;
}

export function changeCode(a: number | null | undefined, b: number | null | undefined) {
    if (a == null || b == null || a === b) {
        return 'center';
    } else {
        return a > b ? 'up' : 'down';
    }
}

export function ratio(numerator: number | null | undefined, denominator: number | null | undefined) {
    if (numerator != null && denominator != null) {
        if (denominator === 0) {
            return 0;
        }

        return numerator / denominator;
    }

    return null;
}

export function subtract(a: number | null | undefined, b: number | null | undefined) {
    if (a != null && b != null) {
        return a - b;
    }

    return null;
}

/**
 * Get the ratio difference between current and previous values such that:
 * currentValue = previousValue * (1 + diffRatio)
 * @returns The difference ratio or null if either input is null.
 */
export function absChangeRatio(currentValue: number | null | undefined, previousValue: number | null | undefined) {
    if (currentValue != null && previousValue != null) {
        if (previousValue === 0) {
            return 0;
        }

        let diff = currentValue - previousValue;
        return Math.abs(diff / previousValue);
    }

    return null;
}

/**
 * Default number formatter for the HAT dashboard.
 */
export function formatNumber(value: number | null | undefined) {
    if (value != null) {
        const digits = value < 1 ? 2 : 0;
        return value.toLocaleString('en', { maximumFractionDigits: digits });
    }
    return '-';
}

/**
 * Default percent formatter for the HAT dashboard.
 * @param value A value in the range 0..1.
 */
export function formatPercent(value: number | null | undefined) {
    if (value != null) {
        value *= 100;
        const digits = value < 10 ? 2 : 0;
        return value.toLocaleString('en', { maximumFractionDigits: digits }) + '%';
    }
    return '-%';
}
